import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Loader2, X, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";

export const DuplicateQuoteDialog = ({ quote, onClose }) => {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [target, setTarget] = useState(quote.dossier_id);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { studio.get("/dossiers").then(({ data }) => setDossiers(Array.isArray(data) ? data : data.items || [])).catch(() => {}); }, []);

  const q = search.trim().toLowerCase();
  const list = dossiers.filter((d) => d.id !== quote.dossier_id && (!q || [d.client_name, d.reference, d.title, d.site_city].some((v) => (v || "").toLowerCase().includes(q)))).slice(0, 8);

  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await studio.post(`/quotes/${quote.id}/duplicate`, { dossier_id: target });
      toast.success(`Devis ${data.number} créé en brouillon à partir de ${quote.number}.`);
      onClose();
      navigate(`/studio/devis/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Duplication impossible.")); setBusy(false); }
  };

  return (
    <div data-testid="duplicate-dialog" className="fixed inset-0 z-[70] flex items-end justify-center bg-[#0F2645]/50 p-0 backdrop-blur-sm sm:items-center sm:p-6" role="dialog">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl sm:p-6" style={{ animation: "devSlideUp .2s ease-out" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-heading text-lg font-bold text-[#0F2645] inline-flex items-center gap-2"><Copy className="h-5 w-5 text-[#3A5CC8]" /> Dupliquer {quote.number}</p>
            <p className="mt-1 text-xs text-[#5C6A87]">Les lignes, prix, conditions et remarques sont copiés dans un nouveau brouillon daté d'aujourd'hui. Choisissez où le créer.</p>
          </div>
          <button type="button" data-testid="duplicate-close" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[#93A0BC] hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        <button type="button" data-testid="duplicate-same-dossier" onClick={() => setTarget(quote.dossier_id)} className={`mt-5 flex w-full items-center gap-3 rounded-xl border p-3 text-left ${target === quote.dossier_id ? "border-[#3A5CC8] bg-[#F6F8FD]" : "border-[#E2E7F7] bg-white"}`}>
          <FolderOpen className="h-4 w-4 shrink-0 text-[#3A5CC8]" />
          <span className="text-sm"><span className="font-semibold text-[#0F2645]">Même dossier · {quote.client_name}</span><span className="block text-xs text-[#5C6A87]">Variante ou nouvelle version pour ce chantier</span></span>
        </button>

        <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Ou un autre dossier (chantier similaire)</p>
        <input data-testid="duplicate-search" className={`${inputCls} mt-2`} placeholder="Rechercher un client, un dossier…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
          {list.map((d) => (
            <button key={d.id} type="button" data-testid={`duplicate-dossier-${d.id}`} onClick={() => setTarget(d.id)} className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm ${target === d.id ? "border-[#3A5CC8] bg-[#F6F8FD]" : "border-[#E2E7F7] bg-white hover:border-[#C9D4F4]"}`}>
              <span className="min-w-0 truncate"><span className="font-semibold text-[#0F2645]">{d.client_name}</span><span className="text-[#93A0BC]"> · {d.reference}{d.title ? ` · ${d.title}` : ""}{d.site_city ? ` · ${d.site_city}` : ""}</span></span>
            </button>
          ))}
          {!list.length && <p className="px-1 py-2 text-xs text-[#93A0BC]">{dossiers.length ? "Aucun autre dossier ne correspond." : "Chargement des dossiers…"}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>Annuler</button>
          <button type="button" data-testid="duplicate-confirm" disabled={busy || !target} onClick={duplicate} className={btnPrimary}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Créer le brouillon
          </button>
        </div>
      </div>
    </div>
  );
};
